import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { AdminAPI, ApiResponse } from '@shared/types/api';

/**
 * Hook to fetch all users with profiles (admin only)
 */
export function useAdminUsers() {
  return useQuery({
    queryKey: ['admin-users'],
    queryFn: async (): Promise<AdminAPI.GetUsersResponse> => {
      return api.get('/admin/users');
    },
  });
}

/**
 * Hook to delete a user (admin only)
 */
export function useDeleteAdminUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string): Promise<ApiResponse<void>> => {
      return api.delete(`/admin/users/${userId}`);
    },
    onSuccess: () => {
      // Invalidate user queries to refresh the list
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
    },
  });
}

/**
 * Hook to fetch the staff notes on a user (admin only). The matching page
 * instead gets notes bundled on the event-details payload; this standalone
 * query backs the user detail page.
 */
export function useAdminUserNotes(userId: string | null) {
  return useQuery({
    queryKey: ['admin-user-notes', userId],
    queryFn: async (): Promise<AdminAPI.GetUserNotesResponse> => {
      return api.get(`/admin/users/${userId}/notes`);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to add a staff note to a user (admin only). Notes surface in the
 * matching UI, the user detail page, and the MCP server; the user never
 * sees them.
 */
export function useCreateUserNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      note,
    }: {
      userId: string;
      note: string;
    }): Promise<AdminAPI.CreateUserNoteResponse> => {
      return api.post(`/admin/users/${userId}/notes`, { note });
    },
    onSuccess: () => {
      // Notes ride along on the event-details payload for the matching page,
      // and on the standalone notes query for the user detail page.
      queryClient.invalidateQueries({ queryKey: ['admin-event'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-notes'] });
    },
  });
}

/**
 * Hook to delete a staff note from a user (admin only)
 */
export function useDeleteUserNote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (noteId: string): Promise<AdminAPI.DeleteUserNoteResponse> => {
      return api.delete(`/admin/user-notes/${noteId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-event'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-notes'] });
    },
  });
}

/**
 * Hook to merge a user into another (admin only). Pass which data types to merge.
 */
export function useMergeUsers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: AdminAPI.MergeUsersRequest): Promise<AdminAPI.MergeUsersResponse> => {
      return api.post('/admin/users/merge', request);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
  });
}

/**
 * Hook to fetch paginated users with summary data (counts, subscription status)
 */
export function useAdminUsersDetailed(params: {
  page: number;
  limit: number;
  search: string;
  sortField: string;
  sortOrder: string;
  subscriptionFilter: string;
  roleFilter?: string;
}) {
  const queryParams = new URLSearchParams({
    page: params.page.toString(),
    limit: params.limit.toString(),
    sortField: params.sortField,
    sortOrder: params.sortOrder,
    subscriptionFilter: params.subscriptionFilter,
    ...(params.roleFilter && { roleFilter: params.roleFilter }),
    ...(params.search && { search: params.search }),
  });

  return useQuery({
    queryKey: ['admin-users-detailed', params],
    queryFn: async (): Promise<AdminAPI.GetUsersDetailedResponse> => {
      return api.get(`/admin/users/detailed?${queryParams.toString()}`);
    },
  });
}

/**
 * Hook to set or clear a user's staff role (admin only).
 * Pass role: null to remove the role.
 */
export function useUpdateUserRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, role }: { userId: string; role: AdminAPI.UserRole | null }): Promise<AdminAPI.UpdateUserRoleResponse> => {
      return api.patch(`/admin/users/${userId}/role`, { role });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
    },
  });
}

/**
 * Hook to fetch single user with full details
 */
export function useAdminUserDetail(userId: string | null) {
  return useQuery({
    queryKey: ['admin-user-detail', userId],
    queryFn: async (): Promise<AdminAPI.GetUserDetailResponse> => {
      return api.get(`/admin/users/${userId}`);
    },
    enabled: !!userId,
  });
}

/**
 * Hook to fetch user history (previous restaurants and tablemates)
 */
export function useAdminUserHistory(userIds: string[], beforeEventTimeId?: string) {
  return useQuery({
    queryKey: ['admin-user-history', userIds.sort().join(','), beforeEventTimeId],
    queryFn: async (): Promise<AdminAPI.GetUserHistoryResponse> => {
      return api.post('/admin/users/history', { userIds, beforeEventTimeId }).then(res => res.data as AdminAPI.GetUserHistoryResponse);
    },
    enabled: userIds.length > 0,
  });
}

/**
 * Hook to update a booking (admin only)
 */
export function useUpdateAdminBooking(eventId?: string) {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      bookingId, 
      data 
    }: { 
      bookingId: string; 
      data: AdminAPI.UpdateBookingRequest;
    }): Promise<AdminAPI.UpdateBookingResponse> => {
      return api.patch(`/admin/bookings/${bookingId}`, data);
    },
    onSuccess: () => {
      // Invalidate user queries to refresh booking data
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
      // Invalidate event details to refresh the matching page
      if (eventId) {
        queryClient.invalidateQueries({ queryKey: ['admin-event', eventId] });
      }
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    },
  });
}

/**
 * Hook to grant a one-time entitlement to a user (admin only)
 */
export function useGrantEntitlement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId: string): Promise<ApiResponse<AdminAPI.AdminUserEntitlement>> => {
      return api.post(`/admin/users/${userId}/entitlements`, {});
    },
    onSuccess: () => {
      // Invalidate user queries to refresh entitlement data
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
    },
  });
}

/**
 * Hook to update an entitlement status (admin only)
 */
export function useUpdateEntitlement() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ entitlementId, status }: { entitlementId: string; status: string }): Promise<ApiResponse<AdminAPI.AdminUserEntitlement>> => {
      return api.patch(`/admin/entitlements/${entitlementId}`, { status });
    },
    onSuccess: () => {
      // Invalidate user queries to refresh entitlement data
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
    },
  });
}

/**
 * Hook to refresh entitlements from RevenueCat for a user (admin only)
 */
export function useRefreshEntitlements() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (userId: string): Promise<ApiResponse<{ synced: number; created: number; updated: number }>> => {
      return api.post(`/admin/users/${userId}/refresh-entitlements`, {});
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
    },
  });
}

/**
 * Hook to extend a user's Stripe subscription by N days (admin only).
 * Pushes their next charge out; stacks with any previous extension. Fails with
 * a 422 for native (App Store / Play Store) subscriptions — grant a one-time
 * entitlement for those instead.
 */
export function useExtendSubscription() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      days,
    }: {
      userId: string;
      days: number;
    }): Promise<AdminAPI.ExtendSubscriptionResponse> => {
      return api.post(`/admin/users/${userId}/extend-subscription`, { days });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
    },
  });
}

/**
 * Hook to create a booking for a user (admin only)
 */
export function useCreateAdminBooking() {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ 
      userId, 
      data 
    }: { 
      userId: string; 
      data: AdminAPI.CreateAdminBookingRequest;
    }): Promise<AdminAPI.CreateAdminBookingResponse> => {
      return api.post(`/admin/users/${userId}/bookings`, data);
    },
    onSuccess: () => {
      // Invalidate user queries to refresh booking data
      queryClient.invalidateQueries({ queryKey: ['admin-user-detail'] });
      queryClient.invalidateQueries({ queryKey: ['admin-users-detailed'] });
      queryClient.invalidateQueries({ queryKey: ['admin-events'] });
    },
  });
}
